// Small controls shared by the sheets and the screens. Every one is at least 48 px high (DESIGN §4).

import type { ComponentChildren } from 'preact';
import { useId } from 'preact/hooks';

interface SwitchProps {
  label: string;
  hint?: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (on: boolean) => void;
}

/**
 * A row that switches something on and off. Its name is the label and the hint is its description, so a screen reader says
 * "Keep on this device, switch, on" and then the hint (not one long name made of both).
 */
export function Switch({ label, hint, checked, disabled, onChange }: SwitchProps) {
  const id = useId();
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-labelledby={`${id}-label`}
      aria-describedby={hint ? `${id}-hint` : undefined}
      disabled={disabled}
      class="switch"
      onClick={() => onChange(!checked)}
    >
      <span class="switch-text">
        <span class="switch-label" id={`${id}-label`}>{label}</span>
        {hint && <span class="switch-hint" id={`${id}-hint`}>{hint}</span>}
      </span>
      <span class="switch-knob" aria-hidden="true" data-on={checked ? '' : undefined} />
    </button>
  );
}

interface ChoiceProps<T extends string | number> {
  /** The name of the group, for screen readers. */
  label: string;
  value: T | null;
  options: readonly { value: T; label: string; name?: string }[];
  onChange: (value: T) => void;
  disabled?: boolean;
}

/** A row of exclusive choices (a radio group drawn as buttons). */
export function Choice<T extends string | number>({ label, value, options, onChange, disabled }: ChoiceProps<T>) {
  return (
    <div class="choice" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button
          type="button"
          role="radio"
          key={o.value}
          aria-checked={o.value === value}
          aria-label={o.name}
          disabled={disabled}
          class="btn"
          data-on={o.value === value ? '' : undefined}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

interface OptionProps {
  group: string;
  label: string;
  hint?: string;
  checked: boolean;
  onChange: () => void;
}

/** A labelled radio button as a whole row. */
export function Option({ group, label, hint, checked, onChange }: OptionProps) {
  return (
    <label class="option">
      <input type="radio" name={group} checked={checked} onChange={onChange} />
      <span class="option-text">
        <span>{label}</span>
        {hint && <span class="option-hint">{hint}</span>}
      </span>
    </label>
  );
}

export function Field({ label, children }: { label: string; children: ComponentChildren }) {
  return (
    <div class="field">
      <div class="field-label">{label}</div>
      {children}
    </div>
  );
}
